<template>
    <div class="payment-schedule">
        <div class="container-width">
            <h2 class="section-title">{{ section3HeadersAndSubHeaders.section3HeaderText }}</h2>
            <p class="sub-heading">{{ section3HeadersAndSubHeaders.section3SubHeaderText }}</p>

            <div class="payment-chart">
                <ul class="tab-name">
                    <li :class="{'active': TimeToDebtFree}" @click="getTabDetails('time-to-debt-free')">
                        {{ section3HeadersAndSubHeaders.section3Tab1HeaderText }}
                    </li>
                    <li :class="{'active': MonthlyPayment}" @click="getTabDetails('monthly-payment')">
                        {{ section3HeadersAndSubHeaders.section3Tab2HeaderText }}
                    </li>
                    <li :class="{'active': HighestSavings}" @click="getTabDetails('highest-savings')">
                        {{ section3HeadersAndSubHeaders.section3Tab3HeaderText }}
                    </li>
                </ul>

                <div v-if="Mappingdata" class="tab-content">
                    <div class="row" :class="{'active': TimeToDebtFree}">
                        <div class="bar-chart">
                            <div>
                                <vue-chart
                                    v-if="TimeToDebtFree && showChart"
                                    id="chartTimeToDebtFree"
                                    :data="Mappingdata.chartTimeToDebtFreeGraph"
                                    :height="ChartHeight"
                                    :options="debtFreeMonthsChartOptions"
                                    :width="ChartWidth"
                                    type="horizontalBar"
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 payment-details-static">
                            <div v-for="(data, index) in Mappingdata.timeToDebtFreeData" :key="index" class="row">
                                <div class="col-xs-6 payment-option-header text-left">
                                    <div class="form-group">
                                        <span>{{ data.name }}</span>
                                    </div>
                                </div>
                                <div class="col-xs-6">
                                    <div class="form-group text-right">
                                        <span class="payment-amount">{{ monthToYearMonthFormatter(data.months) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row" :class="{'active': MonthlyPayment}">
                        <div class="bar-chart">
                            <div>
                                <vue-chart
                                    v-if="MonthlyPayment"
                                    id="chartMonthlyPayment"
                                    :data="Mappingdata.chartMonthlyPayScheduleGraph"
                                    :height="ChartHeight"
                                    :options="monthlyPaymentChartOptions"
                                    :width="ChartWidth"
                                    type="horizontalBar"
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 payment-details-static">
                            <div v-for="(data, index) in Mappingdata.monthlyPaymentData" :key="index" class="row">
                                <div class="col-xs-6 payment-option-header text-left">
                                    <div class="form-group">
                                        <span>{{ data.name }}</span>
                                    </div>
                                </div>
                                <div class="col-xs-6">
                                    <div class="form-group text-right">
                                        <span class="payment-amount">{{ currencyFilter(data.amount) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row" :class="{'active': HighestSavings}">
                        <div class="bar-chart">
                            <div>
                                <vue-chart
                                    v-if="HighestSavings"
                                    id="chartHighestSavings"
                                    :data="Mappingdata.chartHighestSavingsGraph"
                                    :height="ChartHeight"
                                    :options="highestSavingsChartOptions"
                                    :width="ChartWidth"
                                    type="horizontalBar"
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 payment-details-static">
                            <div v-for="(data, index) in Mappingdata.highestSavingsData" :key="index" class="row">
                                <div class="col-xs-6 payment-option-header text-left">
                                    <div class="form-group">
                                        <span>{{ data.name }}</span>
                                    </div>
                                </div>
                                <div class="col-xs-6">
                                    <div class="form-group text-right">
                                        <span class="payment-amount">{{ overallHighestSaving(data.amount) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="chart-table-divider"><hr /></div>
    </div>
</template>
<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import { isNullOrUndefined } from '~/helpers/util';

    const monthToYearMonthFormatter = (months) => {
        // eslint-disable-next-line eqeqeq
        if (isNullOrUndefined(months) || months === '0' || months == 0) return months == 0 ? '0 mos.' : 'N/A';

        const year = Math.floor(parseInt(months, 10) / 12);
        const month = parseInt(months, 10) % 12;
        const monthText = month > 0 ? month > 1 ? `${month} mos.` : `${month} mo.` : '';
        const yearText = year > 0 ? year > 1 ? `${year} yrs` : `${year} yr` : '';
        return monthText === '' ? yearText : `${yearText} ${monthText}`;
    };

    export default {
        props: {
            mappingData: {
                type: Object,
                default() {
                    return {
                        chartTimeToDebtFreeGraph: {},
                        chartMonthlyPayScheduleGraph: {},
                        chartHighestSavingsGraph: {},
                        timeToDebtFreeData: [],
                        monthlyPaymentData: [],
                        highestSavingsData: [],
                    };
                },
            },
            section3HeadersAndSubHeaders: {
                default() {
                    return {
                        section3HeaderText: 'Time vs. Money',
                        section3SubHeaderText: 'Everyone has their most important factor when it comes to debt relief, some people want it done the fastest, others want the lowest payment, and some want the lowest overall cost. See how your options above compare by choosing your most important factor.',
                        section3Tab1HeaderText: 'Time to be Debt Free',
                        section3Tab2HeaderText: 'Monthly Payment',
                        section3Tab3HeaderText: 'Highest Savings',
                    };
                },
            },
        },
        methods: {
            getTabDetails(tabName) {
                if (tabName === 'time-to-debt-free') {
                    this.TimeToDebtFree = true;
                    this.MonthlyPayment = false;
                    this.HighestSavings = false;
                } else if (tabName === 'monthly-payment') {
                    this.TimeToDebtFree = false;
                    this.MonthlyPayment = true;
                    this.HighestSavings = false;
                } else if (tabName === 'highest-savings') {
                    this.TimeToDebtFree = false;
                    this.MonthlyPayment = false;
                    this.HighestSavings = true;
                }
            },
            currencyFilter(figure) {
                return isNullOrUndefined(figure) ? 'N/A' : `$${Accounting.formatNumber(figure)}`;
            },
            monthToYearMonthFormatter(month) {
                return monthToYearMonthFormatter(month);
            },
            overallHighestSaving(amount) {
                return isNullOrUndefined(amount)
                    ? 'N/A'
                    : !amount
                        ? this.currencyFilter(amount)
                        : `${this.currencyFilter(amount)} overall`;
            },
            quicklyBlinkWithChart() {
                this.showChart = false;
                setTimeout(() => {
                    this.showChart = true;
                });
            },
        },
        watch: {
            mappingDataRefresh() {
                this.getTabDetails('time-to-debt-free');
                this.Mappingdata = this.mappingDataRefresh;
            },
            chartTimeToDebtFreeGraph() {
                this.Mappingdata.chartTimeToDebtFreeGraph = this.chartTimeToDebtFreeGraph;
            },
        },
        computed: {
            mappingDataRefresh() {
                return this.mappingData;
            },
            chartTimeToDebtFreeGraph() { // Tab-1 (Bar Chart)
                this.quicklyBlinkWithChart();
                return this.Mappingdata ? this.Mappingdata.chartTimeToDebtFreeGraph : {};
            },
        },
        data() {
            return {
                Mappingdata: null,
                TimeToDebtFree: true,
                MonthlyPayment: false,
                HighestSavings: false,
                ChartWidth: 1140,
                ChartHeight: 250,
                showChart: false,
                debtFreeMonthsChartOptions: {
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                        display: false,
                    },
                    layout: {
                        padding: {
                            right: 100,
                        },
                    },
                    tooltips: {
                        enabled: false,
                    },
                    scales: {
                        yAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                fontSize: 14,
                            },
                        }],
                        xAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                beginAtZero: true,
                                fontSize: 14,
                                // Include a months sign in the ticks
                                callback(value) {
                                    return `${value} months`;
                                },
                            },
                        }],
                    },
                    plugins: {
                        datalabels: {
                            align: 'end',
                            anchor: 'end',
                            backgroundColor: null,
                            borderColor: null,
                            borderRadius: 4,
                            borderWidth: 1,
                            color: '#595959',
                            font: {
                                size: 13,
                                weight: 600,
                            },
                            offset: 4,
                            padding: 0,
                            formatter(value) {
                                return monthToYearMonthFormatter(value);
                            },
                        },
                    },
                },
                monthlyPaymentChartOptions: {
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                        display: false,
                    },
                    layout: {
                        padding: {
                            right: 50,
                        },
                    },
                    tooltips: {
                        enabled: false,
                    },
                    scales: {
                        yAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                fontSize: 14,
                            },
                        }],
                        xAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                beginAtZero: true,
                                fontSize: 14,
                                // Include a dollar sign in the ticks
                                callback(value) {
                                    return value > 999 ? `$${Accounting.formatNumber(value)}` : `$${value}`;
                                },
                            },
                        }],
                    },
                    plugins: {
                        datalabels: {
                            align: 'end',
                            anchor: 'end',
                            backgroundColor: null,
                            borderColor: null,
                            borderRadius: 4,
                            borderWidth: 1,
                            color: '#595959',
                            font: {
                                size: 13,
                                weight: 600,
                            },
                            offset: 4,
                            padding: 0,
                            formatter(value) {
                                return value === '0' ? 'N/A' : `$${Accounting.formatNumber(value)}`;
                            },
                        },
                    },
                },
                highestSavingsChartOptions: {
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                        display: false,
                    },
                    layout: {
                        padding: {
                            right: 100,
                        },
                    },
                    tooltips: {
                        enabled: false,
                    },
                    scales: {
                        yAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                fontSize: 14,
                            },
                        }],
                        xAxes: [{
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                beginAtZero: true,
                                fontSize: 14,
                                // Include a dollar sign in the ticks
                                callback(value) {
                                    return value > 999 ? `$${Accounting.formatNumber(value)}` : `$${value}`;
                                },
                            },
                        }],
                    },
                    plugins: {
                        datalabels: {
                            align: 'end',
                            anchor: 'end',
                            backgroundColor: null,
                            borderColor: null,
                            borderRadius: 4,
                            borderWidth: 1,
                            color: '#595959',
                            font: {
                                size: 13,
                                weight: 600,
                            },
                            offset: 4,
                            padding: 0,
                            formatter(value) {
                                return value === '0'
                                    ? 'N/A'
                                    : !value
                                        ? `$${Accounting.formatNumber(value)}`
                                        : `$${Accounting.formatNumber(value)} Overall`;
                            },
                        },
                    },
                },
            };
        },
        components: {
            VueChart,
        },
        mixins: [Accounting],
    };
</script>
<style lang="scss" src="../../../assets/styles/amortization-breakdown-graph.scss"></style>
